// Bridges a completed ZITADEL (TIPSSSO) session into TipsTrack's auth surface.
//
// The web app authenticates with a bearer token in localStorage + a User from
// `/auth/me`. An SSO login reuses that surface: the OIDC access token becomes the
// bearer the axios client sends, and `tipstrack_auth_mode=sso` records the session
// origin so logout/401 handling can also clear the OIDC store.
//
// IMPORTANT: the bridged token is NOT trusted until a `/auth/me` probe succeeds
// (see AuthContext) — a ZITADEL identity without a provisioned TipsTrack user must
// land signed-out with an explanation, never in a fabricated session.
import type { User as OidcUser } from 'oidc-client-ts';
import { AUTH_MODE_KEY, clearSsoMarker, isSsoSession, setToken } from '../api/client.js';

export { AUTH_MODE_KEY, clearSsoMarker, isSsoSession };

// Why a verified SSO session was rejected (e.g. the identity has no TipsTrack
// account). Stashed when the /auth/me probe fails so the login page can explain it
// instead of bouncing the user to a blank signed-out screen with no reason.
export const SSO_ERROR_KEY = 'tipstrack_sso_error';

/** Persist the SSO access token + session-origin marker from a freshly loaded/renewed OIDC user. */
export function bridgeSsoSession(oidc: OidcUser): void {
  if (oidc.access_token) {
    setToken(oidc.access_token);
    try {
      localStorage.setItem(AUTH_MODE_KEY, 'sso');
    } catch {
      /* ignore storage failures */
    }
  }
}

export function stashSsoError(message: string): void {
  try {
    sessionStorage.setItem(SSO_ERROR_KEY, message);
  } catch {
    /* ignore */
  }
}

/** Read-and-clear the stashed SSO rejection reason (shown once on the login page). */
export function takeSsoError(): string | null {
  try {
    const msg = sessionStorage.getItem(SSO_ERROR_KEY);
    if (msg) sessionStorage.removeItem(SSO_ERROR_KEY);
    return msg;
  } catch {
    return null;
  }
}
